import {useEffect, useCallback, useRef} from '@wordpress/element';
import {useApiRequests} from '../../../hooks';
import {mapLineItems, mapShippingOptions} from '../helpers';

export const usePaymentCallbacks = (
    {
        paypal,
        shipping,
        billing,
        setPaymentData,
        needsSetupToken,
    }) => {

    const orderIdRef = useRef(null);
    const currentData = useRef({billing, shipping, paypal, needsSetupToken});

    const {
        createOrder,
        onShippingChange
    } = useApiRequests({
        isExpress: true,
        paymentMethod: 'ppcp_googlepay'
    })

    useEffect(() => {
        currentData.current = {billing, shipping, paypal, needsSetupToken};
    }, [
        billing,
        shipping,
        paypal,
        needsSetupToken
    ]);

    /**
     * Handle payment authorization callback from Google Pay
     * This is called when the user authorizes payment in the Google Pay sheet
     */
    const onPaymentAuthorized = useCallback(async (paymentData) => {
        try {
            if (currentData.current.needsSetupToken) {
                setPaymentData({
                    paymentMethodData: paymentData.paymentMethodData
                }, false);

                return {
                    transactionState: 'SUCCESS'
                };
            }

            if (!orderIdRef.current) {
                orderIdRef.current = await createOrder(currentData.current);
            }

            const {paypal} = currentData.current;

            // Confirm the order with PayPal
            const response = await paypal.Googlepay().confirmOrder({
                orderId: orderIdRef.current,
                paymentMethodData: paymentData.paymentMethodData
            });

            setPaymentData({
                orderId: orderIdRef.current,
                confirmation: response
            }, false);

            if (response.status === 'APPROVED') {
                orderIdRef.current = null;
                return {
                    transactionState: 'SUCCESS'
                };
            } else if (response.status === 'PAYER_ACTION_REQUIRED') {
                return {
                    transactionState: 'SUCCESS'
                };
            } else {
                return {
                    transactionState: 'ERROR',
                    error: {
                        intent: 'PAYMENT_AUTHORIZATION',
                        message: 'Payment authorization failed'
                    }
                };
            }
        } catch (error) {
            console.error('Payment authorization error:', error);
            return {
                transactionState: 'ERROR',
                error: {
                    intent: 'PAYMENT_AUTHORIZATION',
                    message: error?.message || 'Payment authorization failed'
                }
            };
        }
    }, [
        createOrder,
        setPaymentData
    ]);

    /**
     * Handle payment data changes (shipping address/option changes)
     * This is called when the user changes shipping address or shipping option in the Google Pay sheet
     */
    const onPaymentDataChanged = useCallback(async (data) => {
        try {
            const shippingAddress = data.shippingAddress;
            const shippingOption = data.shippingOptionData?.id;

            // Format shipping address for WooCommerce
            const address = {
                city: shippingAddress?.locality,
                postcode: shippingAddress?.postalCode,
                state: shippingAddress?.administrativeArea,
                country: shippingAddress?.countryCode
            };

            const props = {address, shipping_method: shippingOption}

            const response = await onShippingChange(props, currentData.current);

            return {
                newTransactionInfo: {
                    totalPrice: response.total,
                    totalPriceStatus: 'FINAL',
                    countryCode: response.countryCode,
                    currencyCode: response.currency,
                    displayItems: mapLineItems(response.lineItems || [])
                },
                ...(response.shipping_options && {
                    newShippingOptionParameters: {
                        shippingOptions: mapShippingOptions(response.shippingOptions, response.currency),
                        ...(response.selectedShippingMethod && {
                            defaultSelectedOptionId: response.selectedShippingMethod
                        })
                    }
                })
            };
        } catch (error) {
            console.error('Payment data changed error:', error);

            // Handle errors based on the error data structure
            if (error?.data?.error) {
                return {
                    error: {
                        reason: error.data.error.reason || 'SHIPPING_ADDRESS_INVALID',
                        message: error.data.error.message || error.message,
                        intent: error.data.error.intent || 'SHIPPING_ADDRESS'
                    }
                };
            }

            return {
                error: {
                    reason: 'OTHER_ERROR',
                    message: error?.message || 'Unable to update payment information',
                    intent: 'SHIPPING_ADDRESS'
                }
            };
        }
    }, [
        onShippingChange
    ]);

    return {
        onPaymentAuthorized,
        onPaymentDataChanged
    };
};

export default usePaymentCallbacks;